"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { ChevronRight, Home } from "lucide-react";
import { countries } from "@/lib/country/registry";
import { calculatorNameMap, categoryMap } from "@/lib/constants/navigation";

export default function Breadcrumbs() {
  const pathname = usePathname();
  if (pathname === "/") return null;

  const pathParts = pathname.split("/").filter((part) => part);
  const items: Array<{ label: string; href: string }> = [];

  let currentHref = "";

  pathParts.forEach((part, index) => {
    currentHref += `/${part}`;
    let href = currentHref;

    // Phantom intermediate routes (Ahrefs-verified 404s in the last crawl):
    // /{cc}/compare has no page — the only compare index is the global /compare,
    // which is also where the country page canonicalizes. Point the crumb there.
    if (part.toLowerCase() === "compare" && index === 1) {
      href = "/compare";
    }
    // /authors has no index page — skip the crumb entirely (Home > Author Name).
    if (part.toLowerCase() === "authors" && index === 0) {
      return;
    }

    let label = part;
    
    // Resolve Country Name
    const country = countries.find((c) => c.code === part.toLowerCase());
    if (country) {
      label = country.name;
    }
    // Custom Daily challenge routes
    else if (part.toLowerCase() === "daily") {
      label = "Daily Challenge";
    }
    else if (part.toLowerCase() === "word") {
      label = "FinWord Game";
    }
    else if (part.toLowerCase() === "quiz") {
      label = "Finance Quiz";
    }
    // Resolve Category Name
    else if (categoryMap[part.toLowerCase()]) {
      label = categoryMap[part.toLowerCase()];
    }
    // Resolve Calculator Name
    else if (calculatorNameMap[part.toLowerCase()]) {
      label = calculatorNameMap[part.toLowerCase()];
    }
    // Fallback formatting (capitalize & remove dashes)
    else {
      label = part
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    }

    items.push({ label, href });
  });

  // Breadcrumb structured data is emitted server-side, per-page: each content
  // page hand-authors a BreadcrumbList in its metadata schema (present in the
  // static HTML crawlers read). This "use client" component renders ONLY the
  // visual nav — emitting a second BreadcrumbList here injected a duplicate
  // after hydration (Ahrefs-flagged duplicate BreadcrumbList).

  return (
    <nav aria-label="Breadcrumb" className="breadcrumbs-container">
      <ol className="breadcrumbs-list">
        <li className="breadcrumbs-item">
          <Link href="/" className="breadcrumbs-link" aria-label="Home">
            <Home size={14} />
          </Link>
        </li>
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          return (
            <li key={item.href} className="breadcrumbs-item">
              <ChevronRight size={14} className="breadcrumbs-separator" />
              {isLast ? (
                <span className="breadcrumbs-current" aria-current="page">
                  {item.label}
                </span>
              ) : (
                <Link href={item.href} className="breadcrumbs-link">
                  {item.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>

      {/* Styles live in globals.css (render-blocking): styled-jsx here is
          client-injected, so the breadcrumb painted as an unstyled vertical
          list at first paint and snapped to a flex row after hydration —
          a layout shift at the top of every content page. */}
    </nav>
  );
}
